import { attr, belongsTo, hasMany } from 'spraypaint';
import RecipesIngredient from './Ingredient';
import RecipesProducer from './Producer';
import ApplicationRecord from './ApplicationRecord';
import Recipe from './Recipe';

const Component = ApplicationRecord.extend({
  attrs: {
    ingredient: belongsTo({ type: RecipesIngredient, persist: false }),
    ingredient_id: attr({ type: Number }),
    producer: belongsTo({ type: RecipesProducer, persist: false }),
    producer_id: attr({ type: Number }),
    status: attr({ type: String }),
    supplier_id: attr({ type: Number }),
    code: attr(),
    name: attr(),
    min_weight_g: attr({ type: Number }),
    max_weight_g: attr({ type: Number }),
    recipe_card_title: attr(),
    shelf_life_days: attr({ type: Number }),
    minimum_temperature: attr({ type: Number }),
    maximum_temperature: attr({ type: Number }),
    packing_location: attr(),
    price: attr({ type: Number }),
    unit: attr({ type: String }),
    unit_quantity: attr({ type: Number }),
    unit_quantity_multiple: attr({ type: Number }),
    unit_discrete: attr({ type: Boolean }),
    recipes: hasMany({ type: Recipe }),
    recipe_portion_components: hasMany({ type: 'recipes-recipe_portion_components' }),
    width_mm: attr({ type: Number }),
    height_mm: attr({ type: Number }),
    depth_mm: attr({ type: Number }),
    primary_packaging: attr(),
    primary_packaging_tare_weight_g: attr({ type: Number }),
    primary_packaging_recycling_method: attr(),
    primary_packaging_recycled_content_percent: attr({ type: Number }),
    secondary_packaging: attr(),
    secondary_packaging_tare_weight_g: attr({ type: Number }),
    secondary_packaging_recycling_method: attr(),
    secondary_packaging_recycled_content_percent: attr({ type: Number }),
    tertiary_packaging: attr(),
    tertiary_packaging_tare_weight_g: attr({ type: Number }),
    tertiary_packaging_recycling_method: attr(),
    tertiary_packaging_recycled_content_percent: attr({ type: Number }),
    plant_count: attr({ type: Number }),
    base_ingredient: attr({ type: String }),
  },
  static: {
    endpoint: '/components',
    jsonapiType: 'recipes-components',
  },
  methods: {
    averageWeight() {
      return (this.max_weight_g + this.min_weight_g) / 2;
    },
    protein() {
      if (typeof this.ingredient === 'undefined') {
        return 0;
      }
      return (this.averageWeight() * this.ingredient.protein) / 100;
    },
    fat() {
      if (typeof this.ingredient === 'undefined') {
        return 0;
      }
      return (this.averageWeight() * this.ingredient.fat) / 100;
    },
    carbohydrates() {
      if (typeof this.ingredient === 'undefined') {
        return 0;
      }
      return (this.averageWeight() * this.ingredient.carbohydrates) / 100;
    },
    calories() {
      if (typeof this.ingredient === 'undefined') {
        return 0;
      }
      return (this.averageWeight() * this.ingredient.calories) / 100;
    },
    sugars() {
      if (typeof this.ingredient === 'undefined') {
        return 0;
      }
      return (this.averageWeight() * this.ingredient.sugars) / 100;
    },
    salt() {
      if (typeof this.ingredient === 'undefined') {
        return 0;
      }
      return (this.averageWeight() * this.ingredient.salt) / 100;
    },
    fibre() {
      if (typeof this.ingredient === 'undefined') {
        return 0;
      }
      return (this.averageWeight() * this.ingredient.fibre) / 100;
    },
    saturated_fat() {
      if (typeof this.ingredient === 'undefined') {
        return 0;
      }
      return (this.averageWeight() * this.ingredient.saturated_fat) / 100;
    },
    volume() {
      if (typeof this.width_mm === 'undefined') {
        throw new Error(`The component ${this.name} has undefined width.`);
      }
      if (typeof this.height_mm === 'undefined') {
        throw new Error(`The component ${this.name} has undefined height.`);
      }
      if (typeof this.depth_mm === 'undefined') {
        throw new Error(`The component ${this.name} has undefined depth.`);
      }
      // these values can be null, in which case volume is 0
      return (this.width_mm) * (this.height_mm) * (this.depth_mm);
    },
    requireCoolBag() {
      if (typeof this.maximum_temperature === 'undefined') {
        throw new Error(`The component ${this.name} has undefined maximum temperature.`);
      }
      if (this.maximum_temperature === null) {
        throw new Error(`The component ${this.name} has null maximum temperature.`);
      }
      return this.maximum_temperature <= 8;
    },
  },
});

export default Component;
